//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { EthAddr } from '@shared/SchemaTypes'
import React, { ReactElement } from 'react'

import { KeyboardArrowDown, KeyboardArrowUp, TrendingUp } from '@mui/icons-material'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import {
  Box,
  Button,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  Typography,
  useTheme,
} from '@mui/material'
import IconButton from '@mui/material/IconButton'
import { ERC20Wei, aprToDisplay, erc20WeiIsSignificant, erc20WeiToCommaDecString, pptToUSDC } from '@shared/conversions'

import ViewLogo from '@react/components/elements/ViewLogo'
import CashOutIcon from '@react/components/icons/CashOutIcon'
import DownLeftArrowIcon from '@react/components/icons/DownLeftArrowIcon'
import ReinvestIcon from '@react/components/icons/ReinvestIcon'
import { OtStoreState, useOtChainState, useOtSelector } from '@react/redux/store'
import {
  PoolAccountCTO,
  PoolBaseAccountCTO,
  PoolBaseOverviewCTO,
  PoolDynamicAccountCTO,
  PoolDynamicOverviewCTO,
  PoolFlexAccountCTO,
  PoolFlexOverviewCTO,
  PoolOverviewCTO,
} from '@shared/sharedTypes'
import { Link, useNavigate } from 'react-router-dom'

import { buildPills } from '@react/components/elements/InterestPills'
import { PoolTypeEnum } from '@shared/Enums'
import { displayName } from '@shared/PoolBaseCTO'
import { ETH_ADDR_ZERO } from '@shared/SchemaTypes'
import { buildLenderPoolSummary } from './lenderUtils'

// Helper function to get user's principal earning interest for a pool
function _getMyPrincipalEarningInterest(
  pool: PoolBaseOverviewCTO,
  accountAddr: string | undefined,
  poolAccountCTOs: any
): number {
  if (!accountAddr || !poolAccountCTOs?.[pool.poolAddr]?.[accountAddr]) {
    return 0
  }

  const accountCTO = poolAccountCTOs[pool.poolAddr][accountAddr]

  if (pool.poolType === PoolTypeEnum.FlexibleRate && 'principalEarningInterest' in accountCTO) {
    return Number(accountCTO.principalEarningInterest || 0)
  } else if (pool.poolType === PoolTypeEnum.Dynamic && 'principalEarningInterest' in accountCTO) {
    return Number(accountCTO.principalEarningInterest || 0)
  } else if (pool.poolType === PoolTypeEnum.Term && 'tokenBalance' in accountCTO) {
    return Number(accountCTO.tokenBalance || 0)
  }

  return 0
}

function InvestmentRow(
  poolBaseAccountCTO: PoolBaseAccountCTO,
  poolBaseOverviewCTO: PoolBaseOverviewCTO,
  theme: Theme,

  navigate: any
): ReactElement {
  let matureDate

  let pills: React.ReactElement | undefined = undefined
  let dollars

  switch (poolBaseOverviewCTO.poolType) {
    case PoolTypeEnum.Term: {
      const poolAccountCTO = poolBaseAccountCTO as PoolAccountCTO
      const poolOverviewCTO = poolBaseOverviewCTO as PoolOverviewCTO
      const investorSummary = buildLenderPoolSummary(poolOverviewCTO, poolAccountCTO)
      matureDate = new Date(investorSummary.matureEthDate * 1e3)

      if (Number(poolAccountCTO.requestedAssetsOf) > 0) {
        pills = (
          <>
            <Chip icon={<DownLeftArrowIcon />} label="Withdraw Requested" />
            <Chip
              sx={{ backgroundColor: theme.palette.accent2.subtle }}
              icon={<CashOutIcon />}
              label={matureDate.toLocaleDateString()}
            />
          </>
        )
      } else {
        pills = (
          <>
            <Chip
              sx={{ backgroundColor: theme.palette.info.subtle, marginRight: 2 }}
              icon={<ReinvestIcon />}
              label={matureDate.toLocaleDateString()}
            />
            <Chip
              sx={{ backgroundColor: theme.palette.success.subtle, marginRight: 2 }}
              icon={<TrendingUp />}
              label={aprToDisplay(poolOverviewCTO.activeLoanState.finalizedApr) + '% APY'}
            />
            {poolOverviewCTO.requestedLoanState.loanAddr !== ETH_ADDR_ZERO && (
              <Chip
                sx={{ backgroundColor: theme.palette.accent1.subtle }}
                icon={<TrendingUp />}
                label={`Indicative ${aprToDisplay(poolOverviewCTO?.requestedLoanState?.indicativeApr)}% APY`}
              />
            )}
          </>
        )
      }

      dollars = pptToUSDC(poolBaseAccountCTO?.tokenBalance, poolOverviewCTO?.exchangeRateAtMaturity)
      break
    }
    case PoolTypeEnum.FlexibleRate: {
      const poolAccountCTOFlex = poolBaseAccountCTO as PoolFlexAccountCTO
      const poolOverviewCTOFlex = poolBaseOverviewCTO as PoolFlexOverviewCTO
      dollars = poolAccountCTOFlex.accountOutstandingLoanPrincipals
      pills = buildPills(poolOverviewCTOFlex, theme)
      break
    }
    case PoolTypeEnum.Dynamic: {
      const poolAccountCTODynamic = poolBaseAccountCTO as PoolDynamicAccountCTO
      const poolOverviewCTODynamic = poolBaseOverviewCTO as PoolDynamicOverviewCTO
      dollars = poolAccountCTODynamic.principalEarningInterest
      pills = buildPills(poolOverviewCTODynamic, theme)
      break
    }
    default:
      break
  }
  return (
    <TableRow
      key={poolBaseOverviewCTO.poolAddr}
      onClick={(_) => {
        navigate(`/pools/${poolBaseOverviewCTO?.poolAddr}`)
      }}
      sx={{
        textDecoration: 'none',
        borderBottom: `1px solid ${theme.palette.grey[200]}`,
        '&:hover': {
          backgroundColor: theme.palette.grey[50],
          cursor: 'pointer',
          transform: 'translateY(-1px)',
          transition: 'all 0.2s ease-in-out',
          boxShadow: theme.shadows[1],
        },
        '&:last-child': {
          borderBottom: 'none',
        },
      }}
    >
      <TableCell key={'logo'} sx={{ width: '8%', padding: '16px 12px' }}>
        <ViewLogo
          size={40}
          style={{
            borderRadius: '8px',
            backgroundColor: theme.palette.background.paper,
          }}
          addrOrId={poolBaseOverviewCTO.poolAddr}
        />
      </TableCell>
      <TableCell key={'name'} align="left" sx={{ width: '35%', padding: '16px 12px' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography
            variant="body1"
            component="div"
            sx={{
              fontWeight: 600,
              color: theme.palette.text.primary,
              textDecoration: 'none',
              fontSize: '0.95rem',
            }}
          >
            {displayName(poolBaseOverviewCTO)}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            component="div"
            sx={{
              fontSize: '0.75rem',
              fontWeight: 500,
            }}
          >
            {poolBaseOverviewCTO.symbol}
          </Typography>
        </Box>
      </TableCell>
      <TableCell key={'type'} align="left" sx={{ width: '15%', padding: '16px 12px' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              px: 1.5,
              py: 0.5,
              border: '1px solid',
              borderColor: theme.palette.grey[300],
              borderRadius: '12px',
              backgroundColor: theme.palette.grey[50],
              width: 'fit-content',
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.7rem',
                fontWeight: 500,
                color: theme.palette.text.secondary,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              {poolBaseOverviewCTO.poolType === PoolTypeEnum.Term
                ? 'Term'
                : poolBaseOverviewCTO.poolType === PoolTypeEnum.FlexibleRate
                  ? 'Flexible'
                  : poolBaseOverviewCTO.poolType === PoolTypeEnum.Dynamic
                    ? 'Dynamic'
                    : 'Other'}
            </Typography>
          </Box>
        </Box>
      </TableCell>
      <TableCell key={'interest'} align="right" sx={{ width: '20%', padding: '16px 12px' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>{pills}</Box>
      </TableCell>
      <TableCell key={'balance'} align="right" sx={{ width: '17%', padding: '16px 12px' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 600,
              color: theme.palette.text.primary,
              fontSize: '0.95rem',
            }}
          >
            ${erc20WeiToCommaDecString(dollars as ERC20Wei)}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              fontSize: '0.7rem',
              fontWeight: 500,
            }}
          >
            {(() => {
              // Use the token balance from the account CTO for the token amount
              const tokenBalance = poolBaseAccountCTO.tokenBalance || '0'
              if (Number(tokenBalance) === 0) {
                return `0.00 ${poolBaseOverviewCTO.liquidityTokenSymbol}`
              }
              return `${erc20WeiToCommaDecString(tokenBalance)} ${poolBaseOverviewCTO.symbol}`
            })()}
          </Typography>
        </Box>
      </TableCell>
      <TableCell key={'nav'} align="right" sx={{ width: '5%', padding: '16px 12px' }}>
        <IconButton
          component={Link}
          to={`/pools/${poolBaseOverviewCTO.poolAddr ?? ''}`}
          aria-label="view pool details"
          size="small"
          sx={{
            color: theme.palette.primary.main,
            backgroundColor: theme.palette.grey[100],
            '&:hover': {
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              transform: 'scale(1.1)',
            },
            transition: 'all 0.2s ease-in-out',
          }}
        >
          <ArrowForwardIosIcon fontSize="small" />
        </IconButton>
      </TableCell>
    </TableRow>
  )
}

export default function LenderInvestmentListSummary(props: { limit?: number }): ReactElement {
  const poolAccountCTOs = useOtSelector((state: OtStoreState) => state.poolState.poolAccountCTOs)
  const poolOverviewCTOs = useOtSelector((state: OtStoreState) => state.poolState.poolOverviewCTOs)

  const {
    userConnection: { accountAddr },
  } = useOtChainState()
  const theme = useTheme()
  const [poolCTOList, setPoolCTOList] = React.useState<PoolBaseAccountCTO[]>([])

  // Sorting state
  const [sortBy, setSortBy] = React.useState<'balance' | 'name' | 'type'>('balance')
  const [sortOrder, setSortOrder] = React.useState<'asc' | 'desc'>('desc')

  const navigate = useNavigate()

  React.useEffect(() => {
    if (poolAccountCTOs && accountAddr) {
      const poolCTOList = Object.values(poolAccountCTOs)
        .map((poolAllAccountCTO: Record<EthAddr, PoolBaseAccountCTO>) => {
          return poolAllAccountCTO[accountAddr]
        })
        .filter((poolCTO: PoolBaseAccountCTO) => {
          return erc20WeiIsSignificant(poolCTO?.tokenBalance ?? '0')
        })
        .sort((a: PoolBaseAccountCTO, b: PoolBaseAccountCTO) => {
          let comparison = 0

          switch (sortBy) {
            case 'balance': {
              comparison = Number(BigInt(a.tokenBalance) - BigInt(b.tokenBalance))
              break
            }
            case 'name': {
              const poolA = poolOverviewCTOs[a.poolAddr]
              const poolB = poolOverviewCTOs[b.poolAddr]
              comparison = displayName(poolA).localeCompare(displayName(poolB))
              break
            }
            case 'type': {
              const poolA = poolOverviewCTOs[a.poolAddr]
              const poolB = poolOverviewCTOs[b.poolAddr]
              const typeA = String(poolA?.poolType || '')
              const typeB = String(poolB?.poolType || '')
              comparison = typeA.localeCompare(typeB)
              break
            }
            default: {
              comparison = Number(BigInt(a.tokenBalance) - BigInt(b.tokenBalance))
            }
          }

          return sortOrder === 'asc' ? comparison : -comparison
        })
      setPoolCTOList(poolCTOList)
    }
  }, [poolAccountCTOs, accountAddr, sortBy, sortOrder, poolOverviewCTOs])

  if (Object.keys(poolAccountCTOs).length === 0) {
    return <div data-testid="pool-list-empty"></div>
  }
  return (
    <Box sx={{ marginTop: 4, marginBottom: 4 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 4,
          padding: '0 8px',
        }}
      >
        <Typography variant="h4" fontWeight="bold">
          Investments
        </Typography>
        {props.limit && (
          <Button variant="text" component={Link} to={'/investments'} sx={{ color: theme.palette.accent1.emphasis }}>
            <Typography variant="bodyBold">View All</Typography>
          </Button>
        )}
      </Box>

      <Paper
        elevation={0}
        sx={{
          backgroundColor: theme.palette.background.paper,
          borderRadius: 2,
          border: `1px solid ${theme.palette.grey[200]}`,
          overflow: 'hidden',
        }}
      >
        <TableContainer sx={{ maxHeight: 800 }}>
          <Table stickyHeader aria-label="sticky table">
            <TableHead>
              <TableRow sx={{ borderBottom: `2px solid ${theme.palette.grey[300]}` }}>
                <TableCell sx={{ width: '8%', padding: '16px 12px', backgroundColor: theme.palette.background.paper }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 600, fontSize: '0.75rem' }}
                  ></Typography>
                </TableCell>
                <TableCell
                  sx={{
                    width: '35%',
                    padding: '16px 12px',
                    backgroundColor: theme.palette.background.paper,
                    cursor: 'pointer',
                    transition: 'background-color 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: theme.palette.grey[50],
                    },
                  }}
                  onClick={() => {
                    if (sortBy === 'name') {
                      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
                    } else {
                      setSortBy('name')
                      setSortOrder('asc')
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                      Pool
                    </Typography>
                    {sortBy === 'name' &&
                      (sortOrder === 'asc' ? (
                        <KeyboardArrowUp fontSize="small" />
                      ) : (
                        <KeyboardArrowDown fontSize="small" />
                      ))}
                  </Box>
                </TableCell>
                <TableCell
                  sx={{
                    width: '15%',
                    padding: '16px 12px',
                    backgroundColor: theme.palette.background.paper,
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: theme.palette.grey[50],
                    },
                  }}
                  onClick={() => {
                    if (sortBy === 'type') {
                      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
                    } else {
                      setSortBy('type')
                      setSortOrder('asc')
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                      Type
                    </Typography>
                    {sortBy === 'type' &&
                      (sortOrder === 'asc' ? (
                        <KeyboardArrowUp fontSize="small" />
                      ) : (
                        <KeyboardArrowDown fontSize="small" />
                      ))}
                  </Box>
                </TableCell>
                <TableCell sx={{ width: '20%', padding: '16px 12px', backgroundColor: theme.palette.background.paper }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                    Interest Rate
                  </Typography>
                </TableCell>
                <TableCell
                  sx={{
                    width: '17%',
                    padding: '16px 12px',
                    backgroundColor: theme.palette.background.paper,
                    cursor: 'pointer',
                    transition: 'background-color 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: theme.palette.grey[50],
                    },
                  }}
                  onClick={() => {
                    if (sortBy === 'balance') {
                      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
                    } else {
                      setSortBy('balance')
                      setSortOrder('desc')
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, paddingX: '24px' }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontWeight: 600, fontSize: '0.75rem', marginLeft: '12px' }}
                    >
                      Balance
                    </Typography>
                    {sortBy === 'balance' &&
                      (sortOrder === 'asc' ? (
                        <KeyboardArrowUp fontSize="small" />
                      ) : (
                        <KeyboardArrowDown fontSize="small" />
                      ))}
                  </Box>
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ width: '5%', padding: '16px 12px', backgroundColor: theme.palette.background.paper }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 600, fontSize: '0.75rem' }}
                  ></Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {poolCTOList
                .filter((poolAccountCTO: PoolBaseAccountCTO) => {
                  if (poolAccountCTO && poolOverviewCTOs[poolAccountCTO.poolAddr]) {
                    return true
                  }
                  return false
                })
                .map((poolAccountCTO: PoolBaseAccountCTO) => {
                  return InvestmentRow(poolAccountCTO, poolOverviewCTOs[poolAccountCTO.poolAddr], theme, navigate)
                })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  )
}
